import {
  Component,
  Input,
  NgZone,
  OnInit,
  HostListener
} from '@angular/core';

import { finalize } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

import {
  ImageDescriptionService,
  DuckResponse
} from '../../services/image-descriptions.service';

import { EditDialogComponent } from '../dialogs/edit-dialog/edit-dialog.component';

declare var FB: any;

@Component({
  selector: 'app-quackstudio',
  templateUrl: './quackstudio.component.html',
  styleUrls: ['./quackstudio.component.scss']
})
export class QuackstudioComponent implements OnInit {
  selectedFile: File | null = null;
  selectedDescriptionType: string = 'caption';

  uploadedImages: { url: string; description: string }[] = [];

  isLoading: boolean = false;
  errorMessage: string = '';

  dropdownOpen: 'desc' | null = null;

  descriptionOptions = [
    { value: 'caption', label: 'Caption' },
    { value: 'summary', label: 'Summary' },
    { value: 'description', label: 'Full' }
  ];

  @Input() shareUrl: string =
    'https://www.facebook.com/sharer/sharer.php?u=https://cognitivefootprints.com';

  @Input() hashtag: string = '';

  constructor(
    private imageDescriptionService: ImageDescriptionService,
    private ngZone: NgZone,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    (window as any).fbAsyncInit = function () {
      FB.init({
        appId: '1696421854561610',
        cookie: true,
        xfbml: true,
        version: 'v22.0'
      });
    };
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.qs-dropdown')) {
      this.dropdownOpen = null;
    }
  }

  toggleDropdown(type: 'desc'): void {
    this.dropdownOpen = this.dropdownOpen === type ? null : type;
  }

  get loadingText(): string {
    return 'Generating your CopyVision result...';
  }

  get selectedDescLabel(): string {
    const found = this.descriptionOptions.find((o) => o.value === this.selectedDescriptionType);
    return found ? found.label : 'Select Option';
  }

  trackByValue(index: number, item: { value: string; label: string }): string {
    return item.value;
  }

  selectDescType(value: string): void {
    this.selectedDescriptionType = value;
    this.dropdownOpen = null;
    this.errorMessage = '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] || null;
    this.errorMessage = '';
  }

  openEditDialog(image: { url: string; description: string }): void {
    const dialogRef = this.dialog.open(EditDialogComponent, {
      width: '1000px',
      height: 'auto',
      data: { description: image.description }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (typeof result === 'string' && result.trim()) {
        image.description = result.trim();
      }
    });
  }

  private toDataUrl(resp: DuckResponse): string {
    const fmt = (resp.image_format || 'jpeg').toLowerCase();
    const mime = fmt === 'jpg' ? 'jpeg' : fmt;

    if (resp.image && resp.image.length > 10) {
      return `data:image/${mime};base64,${resp.image}`;
    }

    return '';
  }

  private getResponseText(resp: DuckResponse): string {
    return (
      (resp.description || '').toString().trim() ||
      (resp.caption || '').toString().trim() ||
      'No description returned.'
    );
  }

  onUpload(): void {
    if (!this.selectedFile) {
      this.errorMessage = 'Please select an image.';
      return;
    }

    const file = this.selectedFile;
    const promptType = this.selectedDescriptionType;

    this.isLoading = true;
    this.errorMessage = '';
    this.uploadedImages = [];

    this.imageDescriptionService
      .fetch35(file, promptType)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (resp: DuckResponse) => {
          this.uploadedImages = [
            {
              url: this.toDataUrl(resp),
              description: this.getResponseText(resp)
            }
          ];
        },
        error: (err: any) => {
          this.errorMessage = err?.message || 'Failed to process image.';
        }
      });
  }

  shareImage(imageUrl: string, description: string): void {
    void imageUrl;

    if (typeof FB === 'undefined') {
      console.error('Facebook SDK not loaded.');
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      FB.ui(
        {
          method: 'share',
          href: this.shareUrl,
          quote: description,
          hashtag: this.hashtag
        },
        (response: any) => {
          if (response && !response.error_message) {
            console.log('Share complete.', response);
          } else {
            console.error('Share error.', response?.error_message);
          }
        }
      );
    });
  }

  shareToX(): void {
    const text = 'Check out ClariChat!';
    const url = 'https://clarichat.ai';
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank');
  }
}